/**
 * Matériel collaborateur — couche d'appel API (boostcore, module `/material`).
 *
 * Pont entre le front V2 et le back P0 (NestJS). Reproduit les endpoints socle :
 * dossiers, collaborateurs (agrégés depuis User), affectation, cycle de statut,
 * catalogue. Pattern aligné sur `contactsApi.ts` (apiFetch + helpers qs/json).
 *
 * ⚠️ Couche prête mais pas encore câblée au store : la bascule de
 * `useOnboardingStore` (mock → API) se fait quand le back est migré + up.
 * Point d'intégration clé : le back identifie le collaborateur par `userUuid`
 * (le front navigue aujourd'hui par email — à aligner sur userUuid à la bascule).
 */
import { apiFetch } from '@/auth/apiFetch'
import type {
  AssetCategory,
  AssetStatus,
  CaseSource,
  CaseStatus,
  CaseType,
  ReplacementSource,
  ReplacementStatus,
} from '@/v2/onboarding/types'

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

function qs(params: Record<string, unknown>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
  if (!entries.length) return ''
  const usp = new URLSearchParams()
  for (const [k, v] of entries) usp.set(k, String(v))
  return `?${usp.toString()}`
}

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`)
  if (res.status === 204) return undefined as unknown as T
  return (await res.json()) as T
}

function post<T>(path: string, body: unknown): Promise<T> {
  return apiFetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }).then(json<T>)
}

function patch<T>(path: string, body: unknown): Promise<T> {
  return apiFetch(path, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }).then(json<T>)
}

/* -------------------------------------------------------------------------- */
/* Types de réponse (alignés sur les DTO back)                                */
/* -------------------------------------------------------------------------- */

export interface ApiCollaboratorIdentity {
  userUuid: string
  email: string | null
  firstName: string | null
  lastName: string | null
  entity: string | null
  jobTitle: string | null
  manager: string | null
  agency: string | null
  mobile: string | null
  phone3cx: string | null
}

export interface ApiAsset {
  id: string
  userUuid: string
  caseId: string | null
  typeCode: string
  label: string
  category: AssetCategory
  status: AssetStatus
  ownerName: string | null
  attributes: Record<string, string>
  orderedAt: string | null
  reminderAt: string | null
  expectedAt: string | null
  deliveredAt: string | null
  returnedAt: string | null
  condition: string | null
  returnCondition: string | null
  notes: string | null
  replacesId: string | null
  createdAt: string
  updatedAt: string
}

export interface ApiReplacementTicket {
  id: string
  reference: string
  userUuid: string
  oldAssetId: string
  newAssetId: string
  status: ReplacementStatus
  reason: string
  source: string
  externalOrder: boolean
  reminderAt: string | null
  notes: string | null
  createdAt: string
}

export interface ApiCase {
  id: string
  reference: string
  type: CaseType
  status: CaseStatus
  source: CaseSource
  baseline: boolean
  userUuid: string
  arrivalDate: string | null
  exitDate: string | null
  comment: string | null
  createdBy: string | null
  createdAt: string
  updatedAt: string
  assets?: ApiAsset[]
  collaborator?: ApiCollaboratorIdentity | null
}

export interface ApiList<T> {
  data: T[]
  total: number
}

export interface ApiCollaboratorRow extends ApiCollaboratorIdentity {
  ownedCount: number
  activeReplacements: number
}

export interface ApiCollaboratorDetail extends ApiCollaboratorIdentity {
  caseIds: string[]
  ownedAssets: ApiAsset[]
  pendingAssets: ApiAsset[]
  tickets: ApiReplacementTicket[]
}

export interface ApiAssetType {
  code: string
  label: string
  category: AssetCategory
  attributeKeys: string[]
}

export interface ApiJobTemplate {
  jobTitle: string
  label: string
  assetTypeCodes: string[]
}

/* -------------------------------------------------------------------------- */
/* Catalogue                                                                  */
/* -------------------------------------------------------------------------- */

export function fetchAssetTypes(signal?: AbortSignal): Promise<ApiAssetType[]> {
  return apiFetch('/material/asset-types', { signal }).then(json<ApiAssetType[]>)
}

export function fetchTemplates(signal?: AbortSignal): Promise<ApiJobTemplate[]> {
  return apiFetch('/material/templates', { signal }).then(json<ApiJobTemplate[]>)
}

/** Recherche d'utilisateurs pour le sélecteur de collaborateur (onboarding/affectation). */
export function searchUsers(q: string, signal?: AbortSignal): Promise<ApiCollaboratorIdentity[]> {
  return apiFetch(`/material/users${qs({ q })}`, { signal }).then(json<ApiCollaboratorIdentity[]>)
}

/* -------------------------------------------------------------------------- */
/* Dossiers                                                                   */
/* -------------------------------------------------------------------------- */

export function fetchCases(params: {
  type?: CaseType
  status?: CaseStatus
  page?: number
  pageSize?: number
  includeBaseline?: boolean
  signal?: AbortSignal
}): Promise<ApiList<ApiCase>> {
  const { type, status, page, pageSize, includeBaseline, signal } = params
  return apiFetch(`/material/cases${qs({ type, status, page, pageSize, includeBaseline })}`, {
    signal,
  }).then(json<ApiList<ApiCase>>)
}

export function fetchCase(id: string, signal?: AbortSignal): Promise<ApiCase> {
  return apiFetch(`/material/cases/${id}`, { signal }).then(json<ApiCase>)
}

export function createCase(input: {
  type: CaseType
  userUuid: string
  arrivalDate?: string | null
  exitDate?: string | null
  comment?: string
  assetTypeCodes?: string[]
}): Promise<ApiCase> {
  return post<ApiCase>('/material/cases', input)
}

export function deleteCase(id: string): Promise<{ ok: boolean }> {
  return apiFetch(`/material/cases/${id}`, { method: 'DELETE' }).then(json<{ ok: boolean }>)
}

/* -------------------------------------------------------------------------- */
/* Collaborateurs                                                             */
/* -------------------------------------------------------------------------- */

export function fetchCollaborators(
  q?: string,
  signal?: AbortSignal,
): Promise<ApiList<ApiCollaboratorRow>> {
  return apiFetch(`/material/collaborators${qs({ q })}`, { signal }).then(
    json<ApiList<ApiCollaboratorRow>>,
  )
}

export function fetchCollaborator(
  userUuid: string,
  signal?: AbortSignal,
): Promise<ApiCollaboratorDetail> {
  return apiFetch(`/material/collaborators/${encodeURIComponent(userUuid)}`, { signal }).then(
    json<ApiCollaboratorDetail>,
  )
}

/** Self-service : l'inventaire du collaborateur courant (lecture seule, sans droit MATERIAL). */
export function fetchMyInventory(signal?: AbortSignal): Promise<ApiCollaboratorDetail> {
  return apiFetch('/material/me', { signal }).then(json<ApiCollaboratorDetail>)
}

export function assignAssets(
  userUuid: string,
  items: Array<{
    typeCode: string
    attributes?: Record<string, string>
    status?: AssetStatus
    ownerName?: string
    condition?: string
  }>,
): Promise<ApiCollaboratorDetail> {
  return post<ApiCollaboratorDetail>(
    `/material/collaborators/${encodeURIComponent(userUuid)}/assets`,
    { items },
  )
}

/* -------------------------------------------------------------------------- */
/* Objet : cycle de statut                                                    */
/* -------------------------------------------------------------------------- */

export function updateAssetStatus(
  assetId: string,
  status: AssetStatus,
  opts?: { reason?: string; condition?: string; returnCondition?: string },
): Promise<ApiAsset> {
  return patch<ApiAsset>(`/material/assets/${assetId}/status`, { status, ...(opts ?? {}) })
}

/** Édite un objet (admin) : champs partiels. */
export function updateAsset(
  assetId: string,
  changes: {
    label?: string
    ownerName?: string
    status?: AssetStatus
    attributes?: Record<string, string>
    notes?: string
    reminderAt?: string | null
    condition?: string | null
    returnCondition?: string | null
    orderedAt?: string | null
    expectedAt?: string | null
    deliveredAt?: string | null
    returnedAt?: string | null
  },
): Promise<ApiAsset> {
  return patch<ApiAsset>(`/material/assets/${assetId}`, changes)
}

/** Supprime un objet (admin). */
export function deleteAsset(assetId: string): Promise<{ ok: boolean }> {
  return apiFetch(`/material/assets/${assetId}`, { method: 'DELETE' }).then(json<{ ok: boolean }>)
}

/* -------------------------------------------------------------------------- */
/* Remplacements                                                              */
/* -------------------------------------------------------------------------- */

/** Ticket enrichi pour l'affichage (collaborateur + objets liés). */
export interface ApiReplacementRow extends ApiReplacementTicket {
  collaborator?: { userUuid: string; firstName: string | null; lastName: string | null } | null
  oldAsset?: { id: string; label: string; attributes: Record<string, string> } | null
  newAsset?: { id: string; label: string; attributes: Record<string, string> } | null
}

export function fetchReplacements(
  activeOnly?: boolean,
  signal?: AbortSignal,
): Promise<ApiReplacementRow[]> {
  return apiFetch(`/material/replacements${qs({ active: activeOnly })}`, { signal }).then(
    json<ApiReplacementRow[]>,
  )
}

export function requestReplacement(input: {
  userUuid: string
  oldAssetId: string
  reason: string
  source: ReplacementSource
  externalOrder: boolean
  reminderAt?: string | null
  notes?: string
}): Promise<ApiReplacementTicket> {
  return post<ApiReplacementTicket>('/material/replacements', input)
}

export function advanceReplacement(id: string): Promise<ApiReplacementTicket> {
  return post<ApiReplacementTicket>(`/material/replacements/${id}/advance`, {})
}

export function cancelReplacement(id: string): Promise<ApiReplacementTicket> {
  return post<ApiReplacementTicket>(`/material/replacements/${id}/cancel`, {})
}
